import type { SerializedLeaveRequest } from "services/leave-requests.service";
import type { LeaveRecord, LeaveRequestStatus } from "features/leave-calendar/leaveCalendar.types";
import { leaveTypeCodeToVisual } from "./leaveTypeMapping";

export function serializedLeaveToRecord(s: SerializedLeaveRequest): LeaveRecord {
  const row: LeaveRecord = {
    id: s.id,
    userId: s.userId,
    type: leaveTypeCodeToVisual(s.leaveType),
    leaveType: s.leaveType,
    startDate: s.startDate,
    endDate: s.endDate,
    duration: s.duration,
    startTime: s.startTime,
    endTime: s.endTime,
    status: s.status as LeaveRequestStatus | undefined,
    fileId: s.fileId,
    fileCategory: s.fileCategory,
    file: s.file ?? null,
    comment: s.comment || undefined,
    rejectionReason: s.rejectionReason,
    isRedacted: !!s.isRedacted,
  };
  return row;
}

/** Fields a redacted leave row sends as null (see SerializedLeaveRequest.isRedacted). */
const REDACTED_LEAVE_FIELDS = ["fileId", "fileCategory", "file", "rejectionReason"] as const;

/**
 * Apply a realtime leave-updated payload to the row a modal already shows.
 * The socket always carries the redacted shape, so a redacted payload must
 * not wipe the document / signers / rejection reason a privileged viewer
 * already has: only its other fields (status, dates, type…) are taken. The
 * full row is then re-read by id.
 */
export function mergeRealtimeLeaveUpdate<T extends { isRedacted?: boolean }>(
  current: T | null,
  incoming: SerializedLeaveRequest
): T | SerializedLeaveRequest {
  if (!current || !incoming.isRedacted || current.isRedacted) return incoming;
  const safe: Record<string, unknown> = { ...incoming };
  for (const key of REDACTED_LEAVE_FIELDS) delete safe[key];
  delete safe.isRedacted;
  return { ...current, ...safe };
}
